<template>
  <div
    v-loading="loading"
    class="max-w-360 m-auto w-full h-full flex flex-col items-center justify-center"
  >
    <div class="flex-1 flex items-center">
      <ScreenCarousel
        v-model:page="activePage"
        :items="carouselItems"
        :is-edit="isEdit"
        :can-create="canCreate"
        :show-upload="activePageItem?.kind !== 'mouse-rt'"
        :upload-disabled="!enableUpload || uploading"
        @upload="handleUpload"
        @create="handleCreate"
        @delete="deletePage"
      />
    </div>

    <div class="shrink-0 mb-2 w-335 h-82 min-h-fit flex flex-col items-center gap-4.5">
      <div class="p-0 flex items-center gap-4 h-10" />

      <div class="flex gap-2 h-67.5 w-full">
        <div class="p-4 shrink-0 w-59 h-full glass-frosted rounded-6xl">
          <div class="flex items-center gap-2">
            <Icon icon="Function-Wireframe-WindowSize" class="size-4.5 text-black-white" />

            <span class="text-black-white text-m-medium">
              {{ $t('pageAndPlayer.pages.side.title') }}
            </span>
          </div>

          <div class="mt-3 flex flex-col gap-1.5">
            <Button
              v-for="tab in tabs"
              :key="tab.value"
              :active="tab.value === activeTabValue"
              :disabled="tab.disabled"
              color="normal"
              shape="rect"
              class="rounded-l-l rounded-r-l justify-start"
              @click="() => handleSelectStyle(tab.value)"
            >
              <span>{{ $t(tab.label) }}</span>
            </Button>
          </div>
        </div>

        <div class="flex-1 h-full">
          <Transition
            name="fade"
            mode="out-in"
            enter-active-class="animate-in fade-in duration-150"
            leave-active-class="animate-out fade-out duration-150"
          >
            <component :is="activeTabComponent" v-bind="componentProps" v-on="componentEvents" />
          </Transition>
        </div>
      </div>
    </div>

    <Dialog
      v-model:open="isUploadConfirmOpen"
      :title="$t('pageAndPlayer.pages.side.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{ $t('pageAndPlayer.routeDialog.description') }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="confirmLeave">
            {{ $t('pageAndPlayer.routeDialog.cancelText') }}
          </Button>
          <Button color="black" @click="confirmUpload">
            {{ $t('pageAndPlayer.routeDialog.confirmText') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <Dialog
      v-model:open="uploading"
      description=""
      :close-on-overlay-click="false"
      content-class="w-145 p-6 gap-6"
    >
      <rocketProgress v-model="uploadProgress" />
      <div class="text-s-medium w-full text-center text-text-glass-default" role="status">
        {{ $t('pageAndPlayer.carousel.uploading') }}
        <span class="text-brand-blue1">{{ uploadProgress }}</span> / 100
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, markRaw, ref, watch } from 'vue'
  import { Icon, Button, Dialog } from '@rapoo/element'
  import rocketProgress from '@/components/rocketProgress/index.vue'
  import ScreenCarousel from '../components/ScreenCarousel.vue'
  import { usePages } from '../composables/usePages'
  import { useUploadConfirmOnLeave } from '../composables/useUploadConfirmOnLeave'
  import { useRestoreChildDevice } from '../composables/useRestoreChildDevice'
  import { createScreenPictureItem, isCustomSlot, isHomeSlot } from '../utils/pages'
  import { savePicture } from '../utils/screenPictureDb'
  import {
    createDefaultScreenTextStyle,
    renderScreenTextToBlob,
    type ScreenTextStyle,
  } from '../utils/screenText'
  import type { ScreenPictureItem } from '../utils/types'

  useRestoreChildDevice()

  const pageTabs = {
    preset: {
      label: 'pageAndPlayer.pages.side.tabPreset',
      value: 'preset',
      component: markRaw(defineAsyncComponent(() => import('./components/StylePreset.vue'))),
    },
    system: {
      label: 'pageAndPlayer.pages.side.tabPreset',
      value: 'system',
      component: markRaw(defineAsyncComponent(() => import('./components/StyleSystemPage.vue'))),
    },
    picture: {
      label: 'pageAndPlayer.pages.side.tabPicture',
      value: 'picture',
      component: markRaw(defineAsyncComponent(() => import('./components/StylePicture.vue'))),
    },
    text: {
      label: 'pageAndPlayer.pages.side.tabText',
      value: 'text',
      component: markRaw(defineAsyncComponent(() => import('./components/StyleText.vue'))),
    },
  } as const

  const createTab = (key: keyof typeof pageTabs, disabled: boolean) => ({
    ...pageTabs[key],
    disabled,
  })

  const {
    pages,
    pageConfig,
    homePresets,
    activePage,
    pictures,
    pendingHomePresetStyle,
    activeHomePresetStyle,
    selectedPicture,
    loading,
    uploading,
    uploadProgress,
    getPictureList,
    setActivatePreset,
    setNewCarouselPage,
    setPage,
    deletePage,
  } = usePages()

  const activePageItem = computed(() => pages.value.find((page) => page.slot === activePage.value))
  const tabs = computed(() => {
    switch (activePageItem.value?.kind) {
      case 'home':
        return [createTab('preset', false), createTab('picture', true), createTab('text', true)]
      case 'mouse-rt':
        return [createTab('system', false), createTab('picture', true), createTab('text', true)]
      default:
        return [createTab('preset', true), createTab('picture', false), createTab('text', false)]
    }
  })
  const activeTabValue = ref<string>(tabs.value[0]?.value ?? 'picture')
  const textStyle = ref<ScreenTextStyle>(createDefaultScreenTextStyle())
  const textResetKey = ref(0)

  const carouselItems = computed(() => pages.value.filter((page) => page.show))
  const activeTabComponent = computed(() => {
    return tabs.value.find((tab) => tab.value === activeTabValue.value)?.component
  })
  const isEdit = computed(() => isCustomSlot(pageConfig.value, activePage.value))
  const canCreate = computed(() =>
    pages.value.some((page) => isCustomSlot(pageConfig.value, page.slot) && !page.id && !page.show),
  )
  const hasTextContent = computed(() => textStyle.value.content.trim().length > 0)

  const enableUpload = computed(() => {
    if (activeTabValue.value === 'preset') {
      return (
        isHomeSlot(pageConfig.value, activePage.value) &&
        pendingHomePresetStyle.value !== null &&
        pendingHomePresetStyle.value !== activeHomePresetStyle.value
      )
    }

    if (!isEdit.value) return false

    if (activeTabValue.value === 'picture') {
      return selectedPicture.value !== null
    }

    if (activeTabValue.value === 'text') {
      return hasTextContent.value
    }

    return false
  })

  const componentProps = computed(() => {
    if (activeTabValue.value === 'preset') {
      return {
        presets: homePresets.value,
        activePresetStyle: activeHomePresetStyle.value,
        pendingPresetStyle: pendingHomePresetStyle.value,
      }
    }

    if (activeTabValue.value === 'system') {
      return {
        thumbnail: activePageItem.value?.thumbnail ?? '',
      }
    }

    if (activeTabValue.value === 'picture') {
      return {
        pages: pages.value,
        pictures: pictures.value,
        activePage: activePage.value,
        selectedPicture: selectedPicture.value,
      }
    }

    if (activeTabValue.value === 'text') {
      return {
        resetKey: textResetKey.value,
      }
    }

    return {}
  })

  const componentEvents = computed(() => {
    if (activeTabValue.value === 'preset') {
      return {
        select: onSelectPreset,
      }
    }

    if (activeTabValue.value === 'picture') {
      return {
        select: onSelectPicture,
        update: onUpdatePicture,
      }
    }

    if (activeTabValue.value === 'text') {
      return {
        'update:modelValue': onUpdateTextStyle,
      }
    }

    return {}
  })

  const handleSelectStyle = (val: string) => {
    if (tabs.value.find((tab) => tab.value === val)?.disabled !== false) return

    activeTabValue.value = val
  }

  watch(
    tabs,
    (availableTabs) => {
      if (!availableTabs.some((tab) => tab.value === activeTabValue.value && !tab.disabled)) {
        activeTabValue.value =
          availableTabs.find((tab) => !tab.disabled)?.value ?? pageTabs.picture.value
      }
    },
    { immediate: true },
  )

  const handleUpload = async () => {
    if (activeTabValue.value === 'preset') {
      return setActivatePreset()
    }

    if (activeTabValue.value === 'picture') {
      return setPage()
    }

    if (activeTabValue.value === 'text') {
      return uploadTextScreen()
    }
  }

  const handleCreate = () => {
    setNewCarouselPage()
  }

  // 当 upload 可用时离开页面会弹出确认弹窗：离开 / 上传
  const {
    isConfirmDialogOpen: isUploadConfirmOpen,
    confirmLeave,
    confirmUpload,
  } = useUploadConfirmOnLeave(enableUpload, handleUpload)

  /** 文字转图片后保存素材并上传屏显 */
  const uploadTextScreen = async () => {
    if (!isCustomSlot(pageConfig.value, activePage.value) || !hasTextContent.value) return

    const blob = await renderScreenTextToBlob(textStyle.value)
    const pictureItem = createScreenPictureItem({
      blob,
      mimeType: 'image/png',
      pictures: pictures.value,
    })

    await savePicture(pictureItem)
    selectedPicture.value = {
      ...pictureItem,
      url: URL.createObjectURL(pictureItem.blob),
    }

    await setPage()
    await getPictureList()

    textStyle.value = createDefaultScreenTextStyle()
    textResetKey.value++
  }

  const onSelectPreset = (style: number) => {
    pendingHomePresetStyle.value = style
  }

  const onSelectPicture = (item: ScreenPictureItem) => {
    selectedPicture.value = item
  }

  const onUpdatePicture = async () => {
    await getPictureList()
  }

  const onUpdateTextStyle = (style: ScreenTextStyle) => {
    textStyle.value = style
  }
</script>

<style scoped></style>
